import ArticleLayout from '../../components/ArticleLayout';
import RadiatorPrincipFigure from '../../components/figures/RadiatorPrincipFigure';

export default function HvordanVirkerEnRadiator() {
  return (
    <ArticleLayout
      title="Hvordan virker en radiator, en termostat og en varmemåler?"
      metaTitle="Hvordan virker en radiator, termostat og varmemåler?"
      metaDescription="Bliv klog på principperne bag en radiator: konvektion, strålevarme, termostatens mekanik og hvordan elektroniske varmemålere beregner dit forbrug."
      canonicalPath="/hvordan-virker-en-radiator"
      introLead="En vandbåren radiator opvarmer ikke kun boligen ved at udsende direkte strålevarme, men fungerer primært som en naturlig varmluftkonvektor."
      introAnswer="Kold luft langs gulvet suges ind under radiatoren, opvarmes i de indvendige bølgeformede lameller og stiger op under loftet for at cirkulere rundt i stuen. Systemet styres automatisk af termostatens temperaturfølsomme bælg, mens en elektronisk 2-føler måler registrerer det faktiske varmeforbrug."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Sådan sikrer du optimal funktion' },
        { id: 'konvektion', title: '2. Strålevarme kontra konvektion' },
        { id: 'termostat-funktion', title: '3. Sådan fungerer termostaten indeni' },
        { id: 'vaermemaaler', title: '4. Hvordan måler varmemåleren dit forbrug?' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Få radiatoren til at fungere optimalt"
      checklist={[
        {
          title: 'Hold minimum 20 cm frit rum foran og over radiatoren',
          description: 'Møbler og tunge gardiner forhindrer den naturlige konvektionsstrøm og lukker varmen inde.',
        },
        {
          title: 'Støvsug radiatorens indvendige lameller én gang årligt',
          description: 'Et tykt støvlag virker isolerende og nedsætter radiatorens varmeafgivelse med op til 5–10 %.',
        },
        {
          title: 'Hold radiatorkroppen lun i toppen og kold i bunden',
          description: 'Temperaturfaldet gennem radiatoren beviser, at vandets energi effektivt er afleveret til rummet.',
        },
        {
          title: 'Sørg for fri luftcirkulation omkring termostathovedet',
          description: 'Termostaten skal have uhindret kontakt med rumluften for at måle den korrekte temperatur.',
        },
        {
          title: 'Undgå at tildække varmemåleren midt på radiatoren',
          description: 'Varmemåleren skal have frit udsyn til rummet for at beregne temperaturforskellen nøjagtigt.',
        },
      ]}
      diagram={<RadiatorPrincipFigure />}
      sections={[
        {
          id: 'konvektion',
          title: 'Opbygning: Strålevarme mod konvektion',
          content: (
            <>
              <p>
                En moderne stålpanelradiator afgiver varme på to måder:
              </p>
              <p>
                <strong>1. Strålevarme (ca. 30 %):</strong> De glatte for- og bagplader udstråler infrarød varme direkte ud i rummet – på samme måde som solens stråler eller en brændeovn. Det er den varme, du mærker på kroppen, når du står tæt på radiatoren.
              </p>
              <p>
                <strong>2. Konvektionsvarme (ca. 70 %):</strong> Mellem de ydre plader er der påsvejset tynde, bølgeformede konvektionslameller. Disse danner lodrette kanaler. Kold luft suges ind i bunden af radiatoren, varmes hurtigt op i kontakt med det varme stål, stiger til vejrs og strømmer ud gennem topristen. Dette skaber en rolig, behagelig cirkulation af varm luft i hele lokalet.
              </p>
            </>
          ),
        },
        {
          id: 'termostat-funktion',
          title: 'Termostatens indre fysik: Selvvirkende regulering',
          content: (
            <>
              <p>
                En radiatortermostat har intet batteri eller ledning. Den er 100 % mekanisk og selvforsynende med kraft:
              </p>
              <p>
                Inde i termostathovedet findes en bælg af tyndt metal, der er fyldt med en særlig ekspansiv gas eller væske. Når rumtemperaturen stiger, udvider gassen sig og trykker en lille stålstift fremad. Denne stift påvirker ventilkeglen inde i vandrøret og klemmer vandstrømmen til.
              </p>
              <p>
                Når rummet køles af, trækker gassen sig sammen igen, og en kraftig indbygget fjeder skubber stiften ud, så ventilen åbner for mere varmt vand.
              </p>
            </>
          ),
        },
        {
          id: 'vaermemaaler',
          title: 'Hvordan virker varmemåleren på radiatoren?',
          content: (
            <>
              <p>
                I lejligheder og etageejendomme sidder der typisk en lille hvid elektronisk fordelingsmåler (fx Brunata, Techem eller Ista) fastspændt midt på radiatorens forplade:
              </p>
              <p>
                Moderne fordelingsmålere er udstyret med <em>to elektroniske temperatursensorer</em>:
              </p>
              <p>
                • <strong>Sensor 1:</strong> Måler radiatorens overfladetemperatur i bagpladen.
              </p>
              <p>
                • <strong>Sensor 2:</strong> Måler rummets lufttemperatur på forsiden.
              </p>
              <p>
                Målerens mikrochip registrerer kun forbrugsenheder, når der er en reel temperaturforskel – dvs. når radiatoren er markant varmere end rummet. Bliver radiatoren varmet op af sommersolen eller en varm sommerdag, registrerer begge sensorer ens temperatur, og måleren tæller ikke.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Skal radiatoren eller måleren udskiftes?',
        content: (
          <p>
            Varmemålere er plomberede og ejes/administreres af ejendommens varmemålerfirma. Egenhændig afmontering, beskadigelse af plombering eller tildækning er ulovligt og udløser skønsmæssig efterbetaling af varme. Ved udskiftning af selve radiatoren skal målerfirmaet kontaktes for flytning og omregning af målerens skaleringstal.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Tæller varmemåleren, hvis jeg hænger vådt tøj over radiatoren?',
          a: 'Ja! Hvis du dækker måleren til, ophobes der varme omkring føleren, hvilket kan få måleren til at registrere et kunstigt højt varmeforbrug.',
        },
        {
          q: 'Hvad betyder tallene på displayet på varmemåleren?',
          a: 'Displayet veksler typisk mellem det aktuelle forbrug (enheder siden seneste årsopgørelse) og den fastlåste målerstand fra forrige opgørelsesdato.',
        },
        {
          q: 'Hvorfor er radiatoren kold i bunden, når den virker?',
          a: 'Når radiatoren fungerer efter hensigten, har vandet afgivet sin varme til rummet undervejs gennem panelet. Bunden skal være markant køligere end toppen.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-kold-i-bunden', text: 'Hvorfor er radiatoren kold i bunden?', desc: 'korrekt temperaturprofil' },
        { to: '/radiator-termostat', text: 'Radiatortermostater forklaret', desc: 'mekaniske kontra elektroniske modeller' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen', desc: 'undgå møbler foran og forkert drift' },
        { to: '/udluftning-af-radiator', text: 'Udluftning af radiator', desc: 'sikr fuld overfladekontakt med vandet' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
        { url: 'https://www.hofor.dk/privat/fjernvarme/spoergsmaal-svar-fjernvarme-privat/hvad-er-en-varmemaaler/', label: 'Hvad er en varmemåler? - HOFOR' },
      ]}
    />
  );
}
